import { ClientModel } from "../models/clients/client.model";
import { format, getYear, isValid, parse } from "date-fns";
import { GridFilterItem } from "@mui/x-data-grid";
import { MapModel, MarkerModel } from "../models/map/map.model";
import { Marker } from "./app-types";
import {
  showSuccessAlert,
  showErrorAlert,
  showWarningAlert,
} from "../components/shared/alerts";
import { RepeatedType } from "../models/tasks/task-status.enum";
import { TrafficLightParametersModel } from "../models/traffic-light/traffic-light.model";
import { CreateUserModel, CustomUserFormData } from "../models/users/users.model";
import { RolesTypeEnum } from "../models/roles/roles.model";
import { InfoModel } from "../models/info.model";
import { SubTaskViewModel } from "../models/sub-tasks/sub-tasks.model";
import { OutlookAppointment, smartOutlookIntegration } from "./outlook-integration";
import { downloadAuthenticatedFile } from "../api/tasks";
import { BASE_URL } from "../api";
import { SCHEDULE_OPTION_LABELS } from "../constants/schedule-options";
import { PlantsTypeEnum } from "../models/plants/plants.enum";
import { SUBTASK_STATUSES } from "../constants/subtask-statuses";
import {
  addDays,
  getISOWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';
import { TemplateTypeEnum } from "../models/sub-tasks/template.enum";
import { REPORT_TYPES, REPORT_TYPE_LABELS } from "../constants/report-types";

/**
 * Transforms a filter item into a filter object with a field-value pair.
 *
 * @param filterItem - An object of type `GridFilterItem` containing the filter field and value.
 *                     The `field` represents the property name to filter by, and the `value`
 *                     represents the filter criteria.
 *
 * @returns An object with a single key-value pair where the key is the field and the value is the
 *          filter criteria. If the `value` is not a non-empty string, an empty object is returned.
 *
 * @Example
 * ```typescript
 * const filterItem = { field: "name", value: "Alice" };
 * const result = transformFilterItemToFilterObject(filterItem);
 *
 * // Result:
 * // { name: "Alice" }
 *
 * const emptyFilterItem = { field: "name", value: "" };
 * const emptyResult = transformFilterItemToFilterObject(emptyFilterItem);
 *
 * // Result:
 * // {}
 * ```
 */
export const transformFilterItemToFilterObject = (
  filterItem: GridFilterItem
): { field: string; value: any } => {
  const { field, value } = filterItem;
  const result: any = {};

  // Add filters.field and filters.value to result if conditions are met
  if (filterItem && typeof value === "string" && value !== "") {
    result[field as string] = value;
  }

  return result;
};

/**
 * Extracts marker information from building and client data into a unified array of markers.
 *
 * @param buildingData - An array of `MarkerModel` objects containing building information,
 *                       including latitude, longitude, address, and name.
 * @param clientData - A `ClientModel` object containing client information,
 *                     including latitude, longitude, address, and name.
 *
 * @returns An array of `Marker` objects where each marker contains:
 *          - `address_latitude`: The latitude of the location.
 *          - `address_longitude`: The longitude of the location.
 *          - `address`: A formatted string combining the address and name.
 *                     If the address is not available, only the name is used.
 *
 * @Example
 * ```typescript
 * const buildingData = [
 *   { address: "123 Main St", address_latitude: 40.7128, address_longitude: -74.006, name: "Building A" },
 *   { address: null, address_latitude: 34.0522, address_longitude: -118.2437, name: "Building B" },
 * ];
 * const clientData = {
 *   address: "456 Elm St",
 *   address_latitude: 37.7749,
 *   address_longitude: -122.4194,
 *   name: "Client HQ"
 * };
 *
 * const markers = extractMarkers(buildingData, clientData);
 *
 * @Result
 * // [
 * //   { address_latitude: 40.7128, address_longitude: -74.006, address: "123 Main St | Building A" },
 * //   { address_latitude: 34.0522, address_longitude: -118.2437, address: "Building B" },
 * //   { address_latitude: 37.7749, address_longitude: -122.4194, address: "456 Elm St | Client HQ" },
 * // ]
 * ```
 */
export const extractMarkers = (
  buildingData: MarkerModel[],
  clientData: ClientModel
): Marker[] => {
  const markers: MapModel[] = [];

  if (buildingData) {
    buildingData.forEach((item) => {
      const { address, address_latitude, address_longitude, name } = item;
      if (address_latitude !== null || address_latitude !== null) {
        markers.push({
          address_latitude: address_latitude,
          address_longitude: address_longitude,
          address: !address ? name : `${address} | ${name}`,
        });
      }
    });
  }

  if (clientData) {
    const { address, address_latitude, address_longitude, name } = clientData;
    if (address_latitude !== null || address_latitude !== null) {
      markers.push({
        address_latitude: address_latitude,
        address_longitude: address_longitude,
        address: !address ? name : `${address} | ${name}`,
      });
    }
  }

  return markers;
};

/**
 * Filters an object by removing keys specified in the excluded keys array.
 * Nested objects are recursively filtered to exclude the specified keys.
 *
 * @param obj - The input object to be filtered. Can contain nested objects.
 * @param excludedKeys - An array of strings representing the keys to exclude
 *                       from the resulting object.
 *
 * @returns A new object with the excluded keys removed. Nested objects are also
 *          filtered to exclude the specified keys.
 *
 * @Example
 * ```typescript
 * const obj = {
 *   id: 1,
 *   name: "John",
 *   details: {
 *     age: 30,
 *     password: "secret",
 *   },
 *   password: "12345",
 * };
 * const excludedKeys = ["password"];
 *
 * const result = filteredObject(obj, excludedKeys);
 *
 * // Result:
 * // {
 * //   id: 1,
 * //   name: "John",
 * //   details: {
 * //     age: 30,
 * //   },
 * // }
 * ```
 */
export const filteredObject = (obj: any, excludedKeys: string[]) => {
  const filteredObj: any = {};

  for (let key in obj) {
    if (excludedKeys.includes(key)) {
      continue; // Skip excluded keys
    }

    if (typeof obj[key] === "object" && obj[key] !== null) {
      // Recursively filter nested objects
      filteredObj[key] = filteredObject(obj[key], excludedKeys);
    } else {
      filteredObj[key] = obj[key];
    }
  }

  return filteredObj;
};

/**
 * Formats a snake_case string into a human-readable header name by converting
 * underscores to spaces and capitalizing the first letter of each word.
 *
 * @param name - The snake_case string to format. For example, "first_name" or "header_name".
 *
 * @returns A formatted string where each word starts with an uppercase letter,
 *          and underscores are replaced by spaces. For example, "First Name" or "Header Name".
 *
 * @Example
 * ```typescript
 * const headerName = formatHeaderName("first_name");
 * // Result: "First Name"
 *
 * const anotherHeaderName = formatHeaderName("header_name");
 * // Result: "Header Name"
 * ```
 */
export const formatHeaderName = (name: string) => {
  const words = name.split("_");
  const formattedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  );
  return formattedWords.join(" ");
};

/**
 * Retrieves the value of a specified cookie by its name.
 *
 * @param name - The name of the cookie to retrieve. This can be a string or an array (though typically, it should be a string).
 *
 * @returns The value of the cookie as a string if found, or `null` if the cookie does not exist.
 *
 * @Example
 * ```typescript
 * // Assuming document.cookie = "username=JohnDoe; token=abc123";
 * const username = getCookieValue("username");
 * // Result: "JohnDoe"
 *
 * const token = getCookieValue("token");
 * // Result: "abc123"
 *
 * const missingCookie = getCookieValue("nonexistent");
 * // Result: null
 * ```
 */
export const getCookieValue = (name: string | any[]) => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + "=")) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
};

/**
 * Retrieves a specific property value from a cookie by parsing its JSON content.
 *
 * @param {string} cookieName - The name of the cookie to search for.
 * @param {string} propertyName - The property inside the cookie's JSON to extract.
 * @returns {string | null} The value of the specified property, or `null` if not found.
 *
 */
export const getCookieValues = (
  cookieName: string,
  propertyName: string
): string | null => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(cookieName + "=")) {
      const cookieValue = cookie.substring(cookieName.length + 1);
      const cookieObject = JSON.parse(decodeURIComponent(cookieValue));
      const propertyValue = cookieObject[propertyName];
      return propertyValue;
    }
  }
  return null;
};

/**
 * Clears all cookies for the current domain by setting their expiration date to the past.
 *
 * This function iterates through all cookies available in the `document.cookie` string
 * and removes them by setting their value to an empty string and their expiration date
 * to `Thu, 01 Jan 1970 00:00:00 UTC`.
 *
 * @Note
 * This function only clears cookies that are accessible via JavaScript and set for the current domain and path.
 * It does not clear cookies with a different path, domain, or those marked as `HttpOnly`.
 *
 * @Example
 * ```typescript
 * // Assuming document.cookie = "username=JohnDoe; token=abc123";
 * clearAllCookies();
 *
 * // After execution, all cookies will be cleared:
 * // document.cookie = "";
 * ```
 */
export const clearAllCookies = () => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    const equalsPos = cookie.indexOf("=");
    const name = equalsPos > -1 ? cookie.substr(0, equalsPos) : cookie;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  }
};

export const userToken = getCookieValues("_auth_state", "access_token");
export const userFullName = getCookieValues("_auth_state", "fullName");
export const userCookieId = getCookieValues("_auth_state", "id");
export const userRole = getCookieValues("_auth_state", "role");
export const userRoleKey = getCookieValues("_auth_state", "roleKey");
export const clientUserId = getCookieValues("_auth_state","clientUserId");
export const userEmailVerifiedAt = getCookieValues("_auth_state","emailVerifiedAt");

/**
 * Returns only the changed values between the initial and current form values.
 *
 * @template T - The type of the form values.
 * @param {T} values - The current form values.
 * @param {T} initialValues - The initial form values.
 * @returns {Partial<T>} An object containing only the changed fields.
 */
export const getChangedFormValues = <T extends Record<string, any>>(
  values: T,
  initialValues: T
) => {
  return Object.entries(values).reduce((acc: Partial<T>, [key, value]) => {
    const hasChanged =
      JSON.stringify(initialValues[key as keyof T]) !== JSON.stringify(value);
    if (hasChanged) {
      acc[key as keyof T] = value;
    }

    return acc;
  }, {});
};

/**
 * Displays a success, warning, or error notification depending on the type.
 *
 * @param {string | undefined} type - The type of notification ('urgent', 'normal', 'alert').
 * @param {string | undefined} body - The content/message of the notification.
 * @param {string} [title] - Optional title for the notification.
 * @param {boolean} [icon] - Whether to show an icon.
 * @param {boolean} [isHtml=false] - Whether the content should be treated as HTML.
 */
export const showNotification = async (
  type: string | undefined,
  body: string | undefined,
  title?: string | undefined,
  icon?: boolean | undefined,
  isHtml: boolean | undefined = false
) => {
  switch (type) {
    case "urgent":
      await showErrorAlert(body, title, icon, isHtml);
      break;
    case "normal":
      await showSuccessAlert(body, title, icon, isHtml);
      break;
    case "alert":
      await showWarningAlert(body, title, icon, isHtml);
      break;
    default:
      break;
  }
};

/**
 * Formats a date into `yyyy-MM-dd` format.
 *
 * @param {any} date - The date to format.
 * @returns {string | null} The formatted date or null if invalid.
 */
export function formatDateToShortDate(date: any) {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return format(date.getTime(), "yyyy-MM-dd");
  } else {
    return null;
  }
}

/**
 * Formats a date into German date format `dd.MM.yyyy`.
 *
 * @param {any} date - The date to format.
 * @returns {string | null} The formatted date or null if invalid.
 */
export function formatDateToGermanDate(date: any) {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return format(date.getTime(), "dd.MM.yyy");
  } else {
    return null;
  }
}

/**
 * Resolves the date range of the last completed period for a report type.
 *
 * The range always covers the period *before* the one the reference date falls
 * into, e.g. with a reference date of 18.08.2026:
 * - `annual_report`    -> 01.01.2025 - 31.12.2025 (previous year)
 * - `quarterly_report` -> 01.04.2026 - 30.06.2026 (previous quarter, Q2)
 * - `monthly_report`   -> 01.07.2026 - 31.07.2026 (previous month)
 *
 * @param {string} reportType - One of the `REPORT_TYPES` values.
 * @param {Date} [referenceDate=new Date()] - The date the period is derived from.
 * @returns {{ from: Date; to: Date } | null} The period range or null for an unknown type.
 */
export function getReportPeriodRange(
  reportType: string,
  referenceDate: Date = new Date()
): { from: Date; to: Date } | null {
  switch (reportType) {
    case REPORT_TYPES.ANNUAL: {
      const previousYear = subYears(referenceDate, 1);
      return { from: startOfYear(previousYear), to: endOfYear(previousYear) };
    }
    case REPORT_TYPES.QUARTERLY: {
      const previousQuarter = subQuarters(referenceDate, 1);
      return {
        from: startOfQuarter(previousQuarter),
        to: endOfQuarter(previousQuarter),
      };
    }
    case REPORT_TYPES.MONTHLY: {
      const previousMonth = subMonths(referenceDate, 1);
      return { from: startOfMonth(previousMonth), to: endOfMonth(previousMonth) };
    }
    default:
      return null;
  }
}

/**
 * Extracts only the date part from a date-time string.
 *
 * @param {string | null} input - The input date-time string.
 * @returns {string | null} The date part or null.
 */
export function formatDate(input: string | null): string | null {
  if (!input) return null;

  const datePart = input.split(" ")[0];
  return datePart;
}

/**
 * Formats time input into `HH:mm` format.
 *
 * @param {any} time - The time value to format.
 * @returns {string} The formatted time string.
 */
export function formatTimeToShortTime(time: any) {
  // Handle null, undefined, or empty values
  if (!time) {
    return "";
  }

  // Regular expression to check if the time is already formatted as HH:mm
  const formattedTimeRegex = /^\d{2}:\d{2}$/;

  // If the time matches the format, return it directly
  if (formattedTimeRegex.test(time)) {
    return time;
  }

  // Parse and format the time if it's not already formatted
  const parsedTime = isValid(new Date(time))
    ? new Date(time)
    : parse(time, "HH:mm", new Date());
  
  // Check if the parsed time is valid before formatting
  if (!isValid(parsedTime)) {
    return "";
  }
  
  return format(parsedTime, "HH:mm");
}

/**
 * Returns the year from a date.
 *
 * @param {any} date - The date object.
 * @returns {number} The year extracted from the date.
 */
export function formatYear(date: any) {
  return getYear(date);
}

/**
 * Maps a comma-separated string of English day abbreviations to their corresponding German day names.
 *
 * @param commaSeparatedString - A string containing comma-separated English day abbreviations (e.g., "MO,WE,FR").
 *
 * @returns An array of German day names corresponding to the provided abbreviations.
 *          If an abbreviation is not recognized, it is ignored.
 *
 * @Example
 * ```typescript
 * const germanDays = matchNumbersToDays("MO,WE,FR");
 * // Result: ["Montag", "Mittwoch", "Freitag"]
 *
 * const partialDays = matchNumbersToDays("SA,SU");
 * // Result: ["Samstag", "Sonntag"]
 *
 * const invalidDays = matchNumbersToDays("MO,XZ");
 * // Result: ["Montag"] // "XZ" is ignored as it is not a valid day abbreviation.
 * ```
 *
 * @Notes
 * - The valid English day abbreviations are:
 *   - "MO" for Monday ("Montag")
 *   - "TU" for Tuesday ("Dienstag")
 *   - "WE" for Wednesday ("Mittwoch")
 *   - "TH" for Thursday ("Donnerstag")
 *   - "FR" for Friday ("Freitag")
 *   - "SA" for Saturday ("Samstag")
 *   - "SU" for Sunday ("Sonntag")
 * - Unrecognized abbreviations are ignored.
 */
export function matchNumbersToDays(commaSeparatedString: string) {
  // Map of English day abbreviations to German day names
  const dayMapping: { [key: string]: string } = {
    SA: "Samstag",
    SU: "Sonntag",
    MO: "Montag",
    TU: "Dienstag",
    WE: "Mittwoch",
    TH: "Donnerstag",
    FR: "Freitag",
  };

  // Split the input string by commas and map each abbreviation to the German day name
  const selectedDays = commaSeparatedString
    .split(",")
    .map((abbr) => dayMapping[abbr.trim().toUpperCase()] || "Invalid Day");

  return selectedDays.join(", ");
}

/**
 * Maps a key to its human-readable German value.
 *
 * @param {string} key - The key to map.
 * @returns {string} The mapped value.
 */
export const keyToValue = (key: string) => {
  // Define a mapping of keys to values
  const keyToValueMap: any = {
    ph_value_at_20c: "pH-Wert bei 20°C",
    conductivity_at_25c: "elektrische Leitfähigkeit bei 25°C",
    temperature: "Temperatur",
    maximum_temperature: "Maximale Temperatur",
    total_hardness: "Gesamthärte",
    alkalinity: "Alkalität",
    sodium: "Natrium",
    magnesium: "Magnesium",
    chloride: "Chlorid",
    sulfates: "Sulfate",
    total_phosphate_po4: "Phosphat gesamt als PO4",
    date_of_sampling: "Datum der Probennahme",
    iron: "Eisen",
    copper: "Kupfer",
    zinc: "Zink",
    chrome: "Chrom",
    conditioning_agent: "Konditionierungsmittel",
    desalting_machine_fez: "FWZ am Absalzautomat",
    desalination_machine_awz: "AWZ am Absalzautomat",
    fresh_water_meter: "Frischwasserzähler",
    waste_water_meter: "Abwasserzähler",
    biocide_consumed_quantity: "Verbrauchte Menge Biozid",
    hardness_stabilizer_consumed_quantity:
      "Verbrauchte Menge Härtestabilisator",
    ph_value_20c: "pH-Wert bei 20°C",
    ph_value_desalination_machine: "pH-Wert am Absalzautomat",
    conductivity_25c: "elektrische Leitfähigkeit bei 25°C",
    conductivity_continuous_blowdown_machine: "Leitfähigkeit am Absalzautomat",
    m_value: "M-Wert",
    chloride_content: "Chloridgehalt",
    iron_content: "Eisengehalt",
    product_content: "Produktgehalt",
    pseudomonas: "Pseudomonas",
    legionella: "Legionellen",
    colony_count_at_22c: "Koloniezahl bei 22°C",
    colony_count_at_36c: "Koloniezahl bei 36°C",
    low_risk_min: "Hohes Risiko",
    low_warning_min: "Risiko",
    normal_min: "Normal von",
    high_warning_min: "Normal bis",
    high_risk_min: "Risiko",
    high_risk_max: "Hohes Risiko",
    nitrate: "Nitrat",
    sulphite: "Sulfit",
    permanganate_index_o2: "Permanganatindex als O2",
    ammonium: "Ammonium",
    hydrocarbons_wa: "Kohlenwasserstoffe (Wa)",
    toc: "TOC",
    lead: "Blei",
    nickel: "Nickel",
    turbidity: "Trübung",
    nitrite: "Nitrit",
    aox: "AOX",
    acid_capacity_ks8_2: "Säurekapazität KS8,2",
    acid_capacity_up_to_ph_4_3: "Säurekapazität bis pH 4,3 (Ks 4,3)",
    potassium: "Kalium",
    aluminum: "Aluminium",
    manganese: "Mangan",
    base_capacity_kb82: "Basekapazität Kb8,2",
    silicon_sio2: "Silicium als SiO2",
    silicic_acid_sio2: "Kieselsäure (SiO2)",
    molybdenum: "Molybdän",
    ortho_phosphate: "ortho-Phosphat",
    calcium: "Calcium",
    carbonate_hardness: "Karbonathärte",
    total_hardness_cao: "Gesamthärte (als CaO)",
    dip_slide_wert: "Dips-Slide Wert",
    silicate: "Silikat",
    deha: "DEHA",
    phosphate: "o-Phosphat",
    sulfite: "Sulfit",
    p_value: "p-Wert",
    residual_hardness: "Resthärte",
    treated_water_0_5l: "Wasserprobe",
    dip_slide: "Dipslide",
    comments: "Kommentare",
    usable_water_0_5l: "Wasserprobe",
    sample_type: "Probenart",
    main_building_sample_type: "Probenart",
    disinfection_type: "Desinfektionsart",
    active_ingredient: "Biozid, Wirkstoff",
    dosing_interval_last_dosing: "Dosierintervall/letzte Dosierung",
    manual: "Manuell",
    is_pseudomonas: "Pseudomonas",
    total_colony_count: "Gesamtkoloniezahl",
    is_legionella: "Legionellen",
    tranzport_time: "Transportzeit",
    other_cause: "Sonstiger Anlass",
    temperature_monitoring: "Temperaturüberwachung",
    cooling: "Kühlung",
    usable_water: "Wasserprobe",
    dark: "Dunkel",
    monthly: "monatlich",
    time: "Uhrzeit",
    lab_report_name: "Laborbericht",
    status: "Status",
    mixed_water: "Mischwasser",
    component_id: "Komponenten-ID",
    hub: "Hub",
    frequency: "Frequenz",
    in_order: "in Ordnung",
    stock: "Vorrat",
    components: "Komponenten",
    products: "Produkte",
    amount: "Menge",
    plant_product_id: "Anlagenprodukt-ID",
    others: "Sonstiges",
    name: "Name",
    value: "Wert",
    clouding_option: "Trübung",
    odor_option: "Geruch",
    coloring_option: "Färbung",
    why_not_in_order: "Warum nicht in ordnung",
    coloration_turbidity: "Färbung / Trübung",
    clouding_looked_at: "Trübung, betrachted",
    coloring: "Färbung",
    meko: "MEKO",
    poc: "POC",
    dynamic_parameters: "andere Chemikalien",
    traffic_light_id: "Chemikalie",
    order_number: "Bestellnummer",
    visual_inspection: "Sichtkontrolle",
    access_operation_log: "Einsicht Betriebstagebuch",
    dip_slide_value: "Dip Slide Wert",
    coliforme_germs: "Coliforme Keime",
    escherichia_coli: "Escherichia Coli",
    intestinal_enterococci: "Intestinale Enterokokken",
    clostridium_perfringens: "Clostridium Perfringens",
    is_coliforme_germs: "Coliforme Keime",
    is_escherichia_coli: "Escherichia Coli",
    is_intestinal_enterococci: "Intestinale Enterokokken",
    is_clostridium_perfringens: "Clostridium Perfringens",
    time_of_sampling: "Uhrzeit der Probenahme",
    selected_action: "Wie soll es weitergehen",
    time_of_sampling_from: "Uhrzeit der Probenahme",
    time_of_sampling_to: "Uhrzeit bis zur Probenahme",
    coracon_he_6: "Coracon HE 6",
    coracon_ve_9: "Coracon VE 9",
    general_germs_image: "Allgemeine Keime",
    yeasts_image: "Hefen",
    corrosion_test_image: "Korrosionstest",
    additional_image: "Zusätzliches Bild",
    ...SCHEDULE_OPTION_LABELS,
    // Add more key-value pairs as needed
  };

  // Check if the inputText exists in the mapping, return the corresponding value
  if (keyToValueMap.hasOwnProperty(key)) {
    return keyToValueMap[key];
  }

  // Return a default value or null if the inputText is not found
  return key;
};

/**
 * Adjusts the current year to the specified year.
 *
 * @param {number | undefined} year - The year to set.
 * @returns {Date | null} A Date object with the adjusted year or null if undefined.
 */
export const adjustYear = (year: number | undefined) => {
  if (year) {
    const date = new Date();
    date.setFullYear(year);
    return date;
  }
  return null;
};

/**
 * Extracts date and value pairs from an array based on a specified key.
 *
 * @param {any[]} data - The data array.
 * @param {string} key - The key to extract.
 * @returns {Array} An array of [timestamp, value] pairs.
 */
export const getDates = (data: any = [], key: string) => {
  let timeStamp = 1570606650000;
  let dates = [];

  for (let i = 0; i < data.length; i++) {
    timeStamp = new Date(data[i].date_of_sampling).getTime();
    if (data[i][key] !== null && data[i][key] !== undefined) {
      const innerArr = [timeStamp, data[i][key]];
      dates.push(innerArr);
    }
  }
  return dates;
};

/**
 * Formats a number to German locale string with up to 5 decimals.
 *
 * @param {number} [value] - The number to format.
 * @returns {string} Formatted number or "-" if undefined.
 */
export const formatNumberType = (value?: number) => {
  const maximumFractionDigits = 5;
  if (value === 0) return "0";
  if (value) {
    return new Intl.NumberFormat("de-DE", {
      maximumFractionDigits: maximumFractionDigits,
    }).format(value);
  }
  return "-  ";
};

/**
 * Converts German formatted number string to English number.
 *
 * @param {string | number} value - German formatted number.
 * @returns {number} English parsed number.
 */
export const formatNumberToEnglish = (value: string | number): number => {
  if (typeof value === "string") {
    // Convert German-formatted number to a valid float
    value = parseFloat(value.replace(/\./g, "").replace(",", "."));
  }

  if (isNaN(value)) return NaN; // Return NaN if conversion fails

  return value;
};

/**
 * Parses a German float string into a number.
 *
 * @param {any} input - Input to parse.
 * @returns {number} Parsed float number.
 */
export const parseGermanFloat = (input: any) => {
  // Ensure input is a string
  let str = input.toString();

  // Remove dots and commas that are not part of the decimal separator
  // const cleanedString = str.replace(/[^\d,-]/g, "");

  // Replace commas with dots for proper parsing
  const dotSeparatedString = str.replace(/,/g, ".");

  // Parse the float
  const floatValue = parseFloat(dotSeparatedString);

  return floatValue;
};

/**
 * Safely retrieves nested value from object using dot-separated path.
 *
 * @param {any} obj - The object to retrieve from.
 * @param {string} path - Dot-separated path.
 * @returns {any} The nested value or undefined.
 */
export const getNestedValue = (obj: any, path: any) => {
  return path.split(".").reduce((acc: any, part: any) => acc && acc[part], obj);
};

/**
 * Transforms nested objects by replacing objects with `id`/`name` properties into just `id`.
 *
 * @param {Record<string, any>} objects - Input objects.
 * @returns {Record<string, any>} Transformed object.
 */
export function transformObjects(
  objects: Record<string, any>
): Record<string, any> {
  const transformed: Record<string, any> = {};

  for (const [key, value] of Object.entries(objects)) {
    if (value instanceof File) {
      // If the value is a File instance, just copy it as is
      transformed[key] = value;
    } else if (typeof value === "object" && value !== null) {
      transformed[key] = {}; // Initialize the transformed object

      for (const [innerKey, innerValue] of Object.entries(value)) {
        // Check if innerValue is an object and has 'id' and 'name' properties
        if (
          typeof innerValue === "object" &&
          innerValue !== null &&
          "id" in innerValue &&
          "name" in innerValue
        ) {
          // Keep the dynamic name and replace the object with just the 'id'
          transformed[key][innerKey] = innerValue.id;
        } else {
          // If it's not an object or doesn't have 'id' and 'name', leave it unchanged
          transformed[key][innerKey] = innerValue;
        }
      }
    } else {
      // If value is not an object, just copy it as is
      transformed[key] = value;
    }
  }

  return transformed;
}

/**
 * Converts a decimal point to a comma in the given value.
 *
 * @param {string | number} value - The value to be converted. Can be either a string or a number.
 * @returns {string} The converted value with a comma as the decimal separator.
 * - If the value is `0`, it returns `"0"`.
 * - If the value is falsy (e.g., `null` or `undefined`), it returns an empty string.
 * - Otherwise, it converts the value to a string and replaces the decimal point with a comma.
 */

export const convertPointToComma = (value: string | number) => {
  if (value === 0) return "0";
  if (!value) return "";
  return value.toString().replace(".", ",");
};

//Weekdays
export const weekDays = [
  {
    id: "MO",
    name: "Montag",
  },
  {
    id: "TU",
    name: "Dienstag",
  },
  {
    id: "WE",
    name: "Mittwoch",
  },
  {
    id: "TH",
    name: "Donnerstag",
  },
  {
    id: "FR",
    name: "Freitag",
  },
  {
    id: "SA",
    name: "Samstag",
  },
  {
    id: "SU",
    name: "Sonntag",
  },
];

// Semester Options
export const semesterOptions = [
  {
    id: "1",
    name: "Januar/Juli",
  },
  {
    id: "2",
    name: "Februar/August",
  },
  {
    id: "3",
    name: "März/September",
  },
  {
    id: "4",
    name: "April/Oktober",
  },
  {
    id: "5",
    name: "Mai/November",
  },
  {
    id: "6",
    name: "Juni/Dezember",
  },
];

//Yearly Options
export const yearlyOptions = [
  {
    id: "1",
    name: "Januar",
  },
  {
    id: "2",
    name: "Februar",
  },
  {
    id: "3",
    name: "Märzr",
  },
  {
    id: "4",
    name: "April",
  },
  {
    id: "5",
    name: "Mai",
  },
  {
    id: "6",
    name: "Juni",
  },
  {
    id: "7",
    name: "Juli",
  },
  {
    id: "8",
    name: "August",
  },
  {
    id: "9",
    name: "September",
  },
  {
    id: "10",
    name: "Oktober",
  },
  {
    id: "11",
    name: "November",
  },
  {
    id: "12",
    name: "Dezember",
  },
];

/**
 * Finds and returns a human-readable label for a selected repeat value 
 * based on the repetition type (yearly, semester, monthly, weekly, etc.).
 *
 * @param {RepeatedType} type - The type of repetition (e.g., yearly, semester, weekly).
 * @param {string} value - The selected value (e.g., month ID, day ID, or date).
 *
 * @returns {string | undefined} - The human-readable label for the selected repeat value, 
 * or `undefined` if no value is provided.
 */
export const findRepeatedOn = (type: RepeatedType, value: string) => {
  if (!value) return undefined;
  if (type === RepeatedType.jährlich) {
    const rightValue = yearlyOptions.find((item) => item.id === value);
    return rightValue?.name;
  }
  if (type === RepeatedType.semester) {
    const rightValue = semesterOptions.find((item) => item.id === value);
    return rightValue?.name;
  }
  if (type === RepeatedType.monatlich || type === RepeatedType.quartalsweise) {
    return value;
  }
  if (
    type === RepeatedType.wöchentlich ||
    type === RepeatedType.zweiwöchentlich
  ) {
    const rightValue = weekDays.find((item) => item.id === value);
    return rightValue?.name;
  }
};

/**
 * Returns the label ("Monat", "Datum", or "Tag") based on the repetition type.
 *
 * Useful for deciding what kind of input or label should be shown in UI 
 * based on whether the repetition is yearly, monthly, weekly, etc.
 *
 * @param {RepeatedType} type - The type of repetition (e.g., yearly, semester, weekly).
 *
 * @returns {string} - The corresponding label: "Monat", "Datum", or "Tag". 
 * Defaults to an empty string if the type is unrecognized.
 */
export const findRepeatedOnLabel = (type: RepeatedType) => {
  if (type === RepeatedType.jährlich || type === RepeatedType.semester)
    return "Monat";
  if (type === RepeatedType.monatlich || type === RepeatedType.quartalsweise)
    return "Datum";
  if (
    type === RepeatedType.wöchentlich ||
    type === RepeatedType.zweiwöchentlich
  )
    return "Tag";
  return "";
};

/**
 * Maps a VKA (Verfahrenskontrollanalyse) key to the corresponding Traffic Light parameter key.
 *
 * This is useful when transforming field names between VKA templates and the system's traffic light evaluation logic.
 * 
 * If the provided key does not match any mapping, it returns the original key.
 *
 * @param {string} key - The input VKA field key.
 * @returns {string} - The mapped Traffic Light key if found, otherwise the original key.
 */
export const mapVKAKeysToTrafficLight = (key: string) => {
  // Define a mapping of keys to values
  const keyToValueMap: any = {
    ph_value_20c: "ph_value_at_20c",
    conductivity_25c: "conductivity_at_25c",
    chloride_content: "chloride",
    iron_content: "iron",
    product_content: "conditioning_agent",
    temperature: "temperature",
    ph_value_desalination_machine: "ph_value_at_20c",
    conductivity_continuous_blowdown_machine: "conductivity_at_25c",
    m_value: "acid_capacity_up_to_ph_4_3",
    total_hardness: "total_hardness",
    residual_hardness: "total_hardness",
    silicate: "silicic_acid_sio2",
    deha: "deha",
    phosphate: "ortho_phosphate",
    sulfite: "sulphite",
  };

  // Check if the inputText exists in the mapping, return the corresponding value
  if (keyToValueMap.hasOwnProperty(key)) {
    return keyToValueMap[key];
  }

  // Return a default value or null if the inputText is not found
  return key;
};

/**
 * Generates a traffic light configuration object for evaluating a Dip-Slide value
 * against a reference microbiological threshold (e.g. `total_microbiology_at_22_c`).
 *
 * The logic defines three levels:
 * - ✅ Normal (green): Any value less than or equal to the reference value
 * - ⚠️ Warning (yellow): Value greater than reference, but less than or equal to 10 times the reference
 * - ❌ Risk (red): Value greater than 10 times the reference value
 *
 * These ranges are mapped to a `TrafficLightParametersModel`, which can then be used
 * with existing UI logic (e.g. traffic light tooltip renderers).
 *
 * @param dipSlideValue - The numeric Dip-Slide value entered by the user.
 * @param referenceValue - The reference microbiological value used for comparison,
 *                         typically from plant or subtask data.
 *
 * @returns A `TrafficLightParametersModel` object if the reference is valid,
 *          or `null` if the reference is missing or the value is not a number.
 */
export const getDipSlideTrafficData =
  (dipSlideValue: number,
    referenceValue?: number
  ): TrafficLightParametersModel | null => {

    //To handle numbers with German format
    dipSlideValue = formatNumberToEnglish(dipSlideValue);

    if (referenceValue == null || isNaN(dipSlideValue)) return null;

    return {
      normal_min: referenceValue,
      high_risk_min: referenceValue * 100,
      high_risk_max: Number.MAX_SAFE_INTEGER,
    };
  };

/**
* Builds label-value fields array from object keys.
*
* @param {any} values - The data object.
* @param {string[]} keys - Keys to map.
* @returns {Array} Label-value mapped array.
*/
export const buildFieldsFromKeys = (values: any, keys: string[]) => {
  return keys.map((key) => ({
    label: keyToValue(key),
    value: values[key] ?? "-",
  }));
};

/**
 * Formats a given JavaScript Date object into a compact string format: YYMMDD.
 *
 * - `YY` → Last two digits of the year
 * - `MM` → Two-digit month (01–12)
 * - `DD` → Two-digit day (01–31)
 *
 * @param {Date} date - The date to be formatted.
 * @returns {string} A string representing the date in YYMMDD format.
 *
 */
export const formatDateYYMMDD = (date: Date): string => {
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}${month}${day}`;
};

/**
 * Generates a task name based on the current date and optional client, building, and template labels.
 *
 * The format will be: `YYMMDD ClientName BuildingName TemplateLabel`.
 * Each part is included only if a corresponding argument is provided.
 *
 * @param {string} [clientName] - The name of the client (optional).
 * @param {string} [buildingName] - The name of the building (optional).
 * @param {string} [templateLabel] - The name of the task template (optional).
 * @returns {string} A concatenated string representing the generated task name.
 *
 * @example
 * generateTaskName("Acme Corp", "HQ", "Onsite Analysis");
 * // "240612 Acme Corp HQ Onsite Analysis"
 */
export const generateTaskName = (buildingName?: string, templateLabel?: string, dueDays?: number): string => {
  const now = new Date();
  const parsedDays = Number(dueDays ?? 0);
  const dueDate = isNaN(parsedDays) ? new Date() : addDays(new Date(), parsedDays);
  const formattedDate = formatDateYYMMDD(now);
  const weekNumber = "KW" + getISOWeek(dueDate);;
  return `${formattedDate} ${weekNumber ?? ""} ${buildingName ?? ""} ${templateLabel ?? ""}`;
}

/**
 * Formats a date string in `MM-DD` format to a human-readable form like `Jun 12`.
 *
 * @param {string} dateString - A string representing a date in the format `"MM-DD"`.
 * @returns {string} A string with the month abbreviation and day (e.g., "Feb 03").
 *
 * @example
 * formatToMonthNameAndDay("06-12");
 * // "Jun 12"
 */
export const formatToMonthNameAndDay  = (dateString: string) => {
    const [month, day] = dateString.split("-").map(Number);
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthName = monthNames[month - 1];
    return `${monthName} ${day}`;
  };

export function measureTextWidth(text: string, font = "16px Roboto"): number {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 300;
  context.font = font;
  return context.measureText(text).width;
}

// Filter out selected users who are no longer in the responsible users list
export const getFilteredResponsibleUsers = (selectedUserIds: (string | number)[], responsibleUsers: InfoModel[] | undefined): (string | number)[] => {
  if (!responsibleUsers || !Array.isArray(selectedUserIds)) {
    return [];
  }
  
  const validUserIds = responsibleUsers.map(user => user.id);
  return selectedUserIds.filter(userId => validUserIds.includes(userId));
};

export const createMailtoLink = (email: string, subject: string, body: string) => {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);
  return `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`;
};

export const createOutlookAppointment = (
  subTask: SubTaskViewModel,
  title: string,
  date: Date,
  description: string,
  location?: string
) => {
  const appointment: OutlookAppointment = {
    subject: title,
    startTime: date,
    endTime: new Date(date.getTime() + 60 * 60 * 1000),
    body: description,
    location: location || "Beprobungsort",
    attendees: subTask.plant?.property_manager?.email
      ? [subTask.plant.property_manager.email]
      : [],
  };
  smartOutlookIntegration(appointment);
};

export const isVdiPlantType = (plantType: string) => {
  return plantType === PlantsTypeEnum.SystemWaterAnalysisVDI6044 || plantType === PlantsTypeEnum.SystemWaterAnalysisVDI2035;
};

export const isVdi6044PlantType = (plantType: string) => {
  return plantType === PlantsTypeEnum.SystemWaterAnalysisVDI6044;
}

export const isVdi2035PlantType = (plantType: string) => {
  return plantType === PlantsTypeEnum.SystemWaterAnalysisVDI2035;
}

export const isBoilerPlantType = (plantType?: string) => {
  return plantType === PlantsTypeEnum.Kessel || plantType === PlantsTypeEnum.ZusatzwasserKesselwasser;
};

export const isSprinklerPlantType = (plantType?: string) => {
  return plantType === PlantsTypeEnum.Sprinkler;
};

export const isMainBuildingPlantType = (plantType: string) => {
  return plantType === "main_building";
};

export const isGKKLPlantType = (plantType?: string) => {
  return plantType === "closed_cooling_circuit";
};

export const isHeatingWaterPlantType = (plantType?: string) => {
  return plantType === "heating_water";
};

export const generateYearlyPdfReportName = (clientName?: string, plantName?: string, startDate?: any, endDate?: any, reportType?: string) => {
  const reportLabel = (reportType && REPORT_TYPE_LABELS[reportType]) || "Jahresbericht";
  return  `${reportLabel}-${clientName ?? ""}-${plantName ?? ""}-${formatDateToGermanDate(new Date(startDate))}_bis_${formatDateToGermanDate(new Date(endDate))}.pdf`
};

export const handleDownloadPDFClick = async (type?: string, subTasksIDs?: number[], thresholdType?: string, plantType?: string, isPlantInfoKnown?: boolean, taskId?: any) => {
  const params: Record<string, any> = {
    download: true,
    plant_type: plantType,
    threshold_type: thresholdType,
  };

  if (type) params.type = type;
  if (isPlantInfoKnown) params.is_plant_info_known = true;
  if (subTasksIDs) {
    subTasksIDs.forEach((id, index) => {
      params[`sub_tasks_ids[${index}]`] = id;
    });
  }

  await downloadAuthenticatedFile(
    `${BASE_URL}/tasks/${taskId}/report`,
    params,
    'report.pdf'
  );
};

export const getSubtaskLink = (subtask: any) => {
  return `/activity/sub-tasks/${subtask.id}`;
};


// Check if subtask should be colored green (when resolved)
export const shouldColorGreen = (subtask: any) => {
  if (!subtask?.status?.key) return false;
  return subtask.status.key === SUBTASK_STATUSES.RESOLVED;
};


const VOA_TEMPLATE_TYPES: TemplateTypeEnum[] = [
    TemplateTypeEnum.VKA_Vorlage,
    TemplateTypeEnum.InternalOperationalInspection,
    TemplateTypeEnum.BoilerWaterAnalysis,
    TemplateTypeEnum.ChlorineDioxideMeasurement,
    TemplateTypeEnum.ProcessWaterMeasurement,
    TemplateTypeEnum.BIPPlus,
    TemplateTypeEnum.WeeklyAnalysis
];

export const isVOATemplate = (templateType: TemplateTypeEnum) => {
    return VOA_TEMPLATE_TYPES.includes(templateType);
}

export const isBiWeekyAnalysisTemplate = (templateType: TemplateTypeEnum)=>{
  return templateType === TemplateTypeEnum.BiWeeklyAnalysis;
}

export const hasVoaTemplateType = (keys?: TemplateTypeEnum[] | string[]) => {
  if (!Array.isArray(keys)) return false;

  return keys.some(key =>
    VOA_TEMPLATE_TYPES.includes(key as TemplateTypeEnum)
  );
};

/**
 * Normalize a "selected task types" value that may arrive either as an array
 * or as a JSON-encoded string (e.g. from form state or back-end payloads).
 * Returns an empty array when the value is missing or not valid JSON, so it
 * never throws on malformed input.
 */
export const parseTaskTypes = (
  value?: TemplateTypeEnum[] | string[] | string | null
): TemplateTypeEnum[] => {
  if (Array.isArray(value)) return value as TemplateTypeEnum[];
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as TemplateTypeEnum[]) : [];
  } catch {
    return [];
  }
};